import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'

/**
 * Where to send the user after a successful magic-link login, when the login
 * was triggered from a gate (e.g. /login-gate?from=/views). Stored in both
 * sessionStorage (per spec, same-tab) and localStorage (magic links often open
 * in a NEW tab from the mail client — sessionStorage would be lost there).
 * AuthCallback consumes and clears both.
 */
export const LOGIN_REDIRECT_KEY = 'cherry_login_redirect'

export function stashLoginRedirect(path: string): void {
  try {
    sessionStorage.setItem(LOGIN_REDIRECT_KEY, path)
    localStorage.setItem(LOGIN_REDIRECT_KEY, path)
  } catch {
    // Storage unavailable (private mode etc.) — login still works, the
    // redirect simply falls back to /home.
  }
}

export function consumeLoginRedirect(): string | null {
  try {
    const raw = sessionStorage.getItem(LOGIN_REDIRECT_KEY) ?? localStorage.getItem(LOGIN_REDIRECT_KEY)
    sessionStorage.removeItem(LOGIN_REDIRECT_KEY)
    localStorage.removeItem(LOGIN_REDIRECT_KEY)
    // Only allow internal paths (no protocol-relative open redirects).
    if (raw && raw.startsWith('/') && !raw.startsWith('//')) return raw
  } catch {
    // ignore
  }
  return null
}

/**
 * MagicLinkForm — the email → magic-link sign-in form, shared between the
 * public landing page ("/") and the LoginGatePage ("/login-gate"). Extracted so
 * the gate doesn't copy the flow. `fromPath` (optional) is stashed before the
 * link is sent and honoured by AuthCallback after the redirect.
 */
export default function MagicLinkForm({
  fromPath,
  submitLabel,
  className,
}: {
  fromPath?: string
  submitLabel?: string
  className?: string
}) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      if (!email.trim()) return

      setLoading(true)
      try {
        if (fromPath) stashLoginRedirect(fromPath)
        const { error: signInError } = await supabase.auth.signInWithOtp({
          email: email.trim(),
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          },
        })
        if (signInError) throw signInError
        setSent(true)
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : t('auth.error_generic')
        setError(message)
      } finally {
        setLoading(false)
      }
    },
    [email, fromPath, t]
  )

  if (sent) {
    return (
      <div className={`flex flex-col items-center gap-3 ${className ?? ''}`}>
        <p className="text-green text-lg font-body text-center">
          {t('auth.check_inbox')}
        </p>
        <p className="text-sm text-ink-muted text-center">
          {t('auth.link_on_way', { email })}
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={`flex flex-col gap-4 ${className ?? ''}`}>
      <Input
        type="email"
        placeholder={t('auth.email_placeholder')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full h-12 text-base"
        disabled={loading}
        autoComplete="email"
        required
      />

      <Button
        type="submit"
        disabled={loading || !email.trim()}
        className="w-full h-12 btn-primary"
      >
        {loading ? t('auth.sending') : (submitLabel ?? t('auth.send_link'))}
      </Button>

      {error && <p className="text-terra text-sm text-center">{error}</p>}

      <p className="text-sm text-ink-muted text-center leading-relaxed">
        {t('auth.magic_hint')}
      </p>
    </form>
  )
}
